import { addIndex, assoc, complement, filter, has, head, length, map, propOr } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { isNilOrEmpty, isNotNilOrEmpty, mapIndexed } from '@@serverHelpers/index.mjs';

export const useTabs = ({
  grantees,
  getMiddleText,
  defaultMaxVisibleTabs = 5,
  filteredViewMode,
  keepFirstTabFixed = false
}) => {
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(defaultMaxVisibleTabs);
  const [tabs, setTabs] = useState({
    selectedTab: 0,
    tabs: grantees
  });

  const [selectedGrantee, setSelectedGrantee] = useState(head(grantees));
  const [manualTrigger, setManualTrigger] = useState(0);

  const labels = useMemo(() => ({
    adherentLabel: getMiddleText('ADHERENT_GENERAL_INFO_ADHERENT_LABEL'),
    spouseLabel: getMiddleText('BENEFICIAIRE_TYPE_SPOUSE_LABEL'),
    childLabel: getMiddleText('BENEFICIAIRE_TYPE_CHILD_LABEL')
  }), []);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        setMaxVisibleTabs(-1);
      } else if (screenWidth < 992 && defaultMaxVisibleTabs >= 3) {
        setMaxVisibleTabs(3);
      } else if (screenWidth < 1200 && defaultMaxVisibleTabs >= 4) {
        setMaxVisibleTabs(4);
      } else if (screenWidth < 1600 && defaultMaxVisibleTabs >= 5) {
        setMaxVisibleTabs(5);
      } else {
        setMaxVisibleTabs(defaultMaxVisibleTabs);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const beneficiaries = mapIndexed((beneficiary, index) => {
      const isHidden = keepFirstTabFixed ? index > maxVisibleTabs : index + 1 > maxVisibleTabs;

      if (index === 0) {
        return {
          label: labels.adherentLabel,
          index,
          className: keepFirstTabFixed ? '' : isHidden ? 'hidden' : ''
        };
      } else {
        const currentGranteeType = propOr('', 'type', beneficiary);
        const spouseLabel = labels.spouseLabel;
        const childLabel = labels.childLabel;

        let tag = '';

        if (isNotNilOrEmpty(currentGranteeType)) {
          // type is defined => use the corresponding label
          tag = currentGranteeType === 'SPOUSE' ? spouseLabel : childLabel;
          // one of the label is empty => use the other one
        } else if (isNotNilOrEmpty(spouseLabel) && isNilOrEmpty(childLabel)) {
          tag = spouseLabel;
        } else if (isNotNilOrEmpty(childLabel) && isNilOrEmpty(spouseLabel)) {
          tag = childLabel;
        }

        const label = propOr(
          getMiddleText('BENEFICIAIRE_LABEL_TITRE_GLOBAL', { index }),
          'firstName',
          beneficiary
        );
        return {
          label,
          tag,
          index,
          className: isHidden ? 'hidden' : ''
        };
      }
    }, grantees);

    // filter only the customer for tabs in case viewMode === 'CUSTOMER'
    const filteredCustomer = filter(complement(has('tag')), beneficiaries);

    setTabs(assoc('tabs', filteredViewMode ? filteredCustomer : beneficiaries));
  }, [...grantees, manualTrigger, maxVisibleTabs, keepFirstTabFixed]); // Ajout de maxVisibleTabs ici

  const updateVisibility = (direction) => {
    const indexedGrantees = addIndex(map)(
      (grantee, idx) => ({
        ...grantee,
        mapIndex: idx
      }),
      tabs.tabs
    );

    const startIndex = keepFirstTabFixed ? 1 : 0;

    const visibleItems = filter(
      (grantee) => grantee.mapIndex >= startIndex && grantee.className !== 'hidden',
      indexedGrantees
    );

    if (visibleItems.length === 0) return;

    const firstVisibleIdx = visibleItems[0].mapIndex;
    const lastVisibleIdx = visibleItems[visibleItems.length - 1].mapIndex;

    let newSelectedTab = tabs.selectedTab;

    const visibleTabs = addIndex(map)((grantee, idx) => {
      if (keepFirstTabFixed && idx === 0) {
        return {
          ...grantee,
          className: ''
        };
      }

      if (direction === 'right') {
        if (idx === firstVisibleIdx) {
          if (tabs.selectedTab === firstVisibleIdx) {
            newSelectedTab = Math.min(tabs.selectedTab + 1, tabs.tabs.length - 1);
            setSelectedGrantee(tabs.tabs[newSelectedTab]);
          }
          return {
            ...grantee,
            className: 'hidden'
          };
        }
        if (idx === lastVisibleIdx + 1) {
          return {
            ...grantee,
            className: ''
          };
        }
      } else if (direction === 'left') {
        if (idx === lastVisibleIdx) {
          if (tabs.selectedTab === lastVisibleIdx) {
            newSelectedTab = Math.max(tabs.selectedTab - 1, keepFirstTabFixed ? 1 : 0);
            setSelectedGrantee(tabs.tabs[newSelectedTab]);
          }
          return {
            ...grantee,
            className: 'hidden'
          };
        }
        if (idx === firstVisibleIdx - 1 && idx >= startIndex) {
          return {
            ...grantee,
            className: ''
          };
        }
      }
      return grantee;
    }, tabs.tabs);

    setTabs({
      selectedTab: newSelectedTab,
      tabs: visibleTabs
    });
  };

  const isButtonDisabled = (direction) => {
    const indexedGrantees = addIndex(map)(
      (grantee, idx) => ({
        ...grantee,
        mapIndex: idx
      }),
      tabs.tabs
    );

    const startIndex = keepFirstTabFixed ? 1 : 0;

    const visibleItems = filter(
      (grantee) => grantee.mapIndex >= startIndex && grantee.className !== 'hidden',
      indexedGrantees
    );

    if (visibleItems.length === 0) return true;

    const firstVisibleIdx = visibleItems[0].mapIndex;
    const lastVisibleIdx = visibleItems[visibleItems.length - 1].mapIndex;

    if (direction === 'left') return firstVisibleIdx <= startIndex;
    if (direction === 'right') return lastVisibleIdx >= tabs.tabs.length - 1;

    return false;
  };

  const displayTabsPagination = length(tabs.tabs) > maxVisibleTabs;

  return {
    tabs,
    setTabs,
    updateVisibility,
    isButtonDisabled,
    selectedGrantee,
    setSelectedGrantee,
    displayTabsPagination,
    setManualTrigger,
    maxVisibleTabs
  };
};
